/**
 * Components page: Card, Button, Input, Table, Tooltip, Dropdown, Search input, Buttoned lookup, Tabset.
 * All field groups use the same width (--ds-field-width).
 */

import {
	createBadge,
	createCard,
	createButton,
	createCheckbox,
	createInput,
	createTable,
	createTooltip,
	createDropdown,
	createMultiSelect,
	createSearchField,
	createLookupFieldGroup,
	createTabset,
} from "@aiapollo/design-system"
import { ICON_CELL, ICON_SORT_DOWN, ICON_SORT_UP } from "../../icons"

const CARD_PADDINGS = ["xs", "sm", "md", "lg", "xl"] as const
const BUTTON_VARIANTS = ["primary", "secondary", "outline", "ghost"] as const
const BUTTON_SIZES = ["sm", "md", "lg"] as const
const TOOLTIP_POSITIONS = ["top", "right", "bottom", "left"] as const
const DROPDOWN_OPTIONS = [
	{ value: "a", label: "Option A" },
	{ value: "b", label: "Option B" },
	{ value: "c", label: "Option C" },
	{ value: "d", label: "Option D" },
	{ value: "e", label: "Option E" },
	{ value: "f", label: "Option F" },
	{ value: "g", label: "Option G" },
	{ value: "h", label: "Option H" },
]

export function createComponentsPage(): HTMLElement {
	const root = document.createElement("div")
	root.setAttribute("role", "main")

	// Card — all padding variations
	const cardSection = createCard({ title: "Card", padding: "md" })
	const cardDemos = document.createElement("div")
	cardDemos.className = "component-demos"
	for (const padding of CARD_PADDINGS) {
		const demo = createCard({ title: `Padding ${padding}`, padding })
		demo.appendChild(document.createTextNode(`Card with padding="${padding}".`))
		cardDemos.appendChild(demo)
	}
	cardSection.appendChild(cardDemos)
	root.appendChild(cardSection)

	// Button — all variants × sizes + disabled
	const buttonSection = createCard({ title: "Button", padding: "md" })
	const buttonDemos = document.createElement("div")
	buttonDemos.className = "component-demos"
	for (const variant of BUTTON_VARIANTS) {
		for (const size of BUTTON_SIZES) {
			buttonDemos.appendChild(
				createButton({ variant, size, label: `${variant} ${size}` })
			)
		}
	}
	buttonDemos.appendChild(
		createButton({ variant: "primary", size: "md", disabled: true, label: "Disabled" })
	)
	buttonSection.appendChild(buttonDemos)
	root.appendChild(buttonSection)

	// Input — standard size (text, disabled, password, number)
	const inputSection = createCard({ title: "Input", padding: "md" })
	const inputDemos = document.createElement("div")
	inputDemos.className = "component-demos"
	inputDemos.appendChild(
		createInput({ placeholder: "Placeholder", label: "Label" })
	)
	inputDemos.appendChild(
		createInput({ disabled: true, placeholder: "Disabled", label: "Disabled" })
	)
	inputDemos.appendChild(
		createInput({ type: "password", placeholder: "Password", label: "Password" })
	)
	inputDemos.appendChild(
		createInput({ type: "number", placeholder: "Number", value: "0", label: "Number" })
	)
	inputSection.appendChild(inputDemos)
	root.appendChild(inputSection)

	// Table — default, compact, bordered
	const tableSection = createCard({ title: "Table", padding: "md" })
	const tableDemos = document.createElement("div")
	tableDemos.className = "component-demos"
	const headers = ["Name", "Value"]
	const rows = [
		["Alpha", "1"],
		["Beta", "2"],
		["Gamma", "3"],
	]
	tableDemos.appendChild(
		createTable({ headers, rows, variant: "default" })
	)
	tableDemos.appendChild(
		createTable({ headers, rows, variant: "compact" })
	)
	tableDemos.appendChild(
		createTable({ headers, rows, variant: "bordered" })
	)
	tableSection.appendChild(tableDemos)
	root.appendChild(tableSection)

	// Tooltip — top, right, bottom, left
	const tooltipSection = createCard({ title: "Tooltip", padding: "md" })
	const tooltipDemos = document.createElement("div")
	tooltipDemos.className = "component-demos"
	for (const position of TOOLTIP_POSITIONS) {
		const trigger = document.createElement("button")
		trigger.type = "button"
		trigger.className = "btn btn--outline btn--md"
		trigger.textContent = position
		tooltipDemos.appendChild(
			createTooltip({ trigger, text: `Tooltip ${position}`, position })
		)
	}
	tooltipSection.appendChild(tooltipDemos)
	root.appendChild(tooltipSection)

	// Dropdown — single + multi
	const dropdownSection = createCard({ title: "Dropdown", padding: "md" })
	const dropdownDemos = document.createElement("div")
	dropdownDemos.className = "component-demos"
	const singleDropdown = createDropdown({
		id: "demo-single",
		label: "Single",
		options: DROPDOWN_OPTIONS,
		placeholder: "Choose one",
		listboxAriaLabel: "Options",
		mode: "single",
	})
	dropdownDemos.appendChild(singleDropdown.root)
	const multiDropdown = createMultiSelect({
		id: "demo-multi",
		label: "Multi",
		options: DROPDOWN_OPTIONS,
		placeholder: "All",
		listboxAriaLabel: "Options",
		initialSelected: ["a", "b", "c", "d", "e", "f"],
	})
	dropdownDemos.appendChild(multiDropdown.root)

	// Dropdown with trailing toggle icon (e.g. ASC/DESC sort direction)
	const toggleDropdown = createDropdown({
		id: "demo-toggle-icon",
		label: "With toggle icon",
		options: DROPDOWN_OPTIONS,
		placeholder: "Choose one",
		listboxAriaLabel: "Options",
		mode: "single",
		rightToggleIcon: {
			iconUp: ICON_SORT_UP,
			iconDown: ICON_SORT_DOWN,
			initialAsc: true,
			ariaLabelAsc: "Ascending — click to switch to descending",
			ariaLabelDesc: "Descending — click to switch to ascending",
		},
	})
	dropdownDemos.appendChild(toggleDropdown.root)
	dropdownSection.appendChild(dropdownDemos)
	root.appendChild(dropdownSection)

	// Search input — same width as Input/Dropdown
	const searchSection = createCard({ title: "Search input", padding: "md" })
	const searchDemos = document.createElement("div")
	searchDemos.className = "component-demos"
	const searchField = createSearchField({
		label: "Name",
		placeholder: "Type to search…",
		minChars: 2,
		debounceMs: 300,
		maxResults: 10,
		fetchResults: () => Promise.resolve([]),
		getItemLabel: (item) => String((item as { name?: string }).name ?? item),
	})
	searchDemos.appendChild(searchField.root)
	searchSection.appendChild(searchDemos)
	root.appendChild(searchSection)

	// Buttoned lookup — input + lookup button; same total width as other field groups
	const lookupSection = createCard({ title: "Buttoned lookup", padding: "md" })
	const lookupDemos = document.createElement("div")
	lookupDemos.className = "component-demos"
	const lookupField = createLookupFieldGroup({
		label: "Without trailing icon",
		placeholder: "ID or code",
		fetchById: (id) =>
			new Promise((resolve) =>
				setTimeout(() => resolve({ id, label: `Fetched: ${id}` }), 300)
			),
		onResult: () => {},
	})
	lookupDemos.appendChild(lookupField.root)

	// With trailing icon (e.g. "use selected cell")
	const lookupFieldWithIcon = createLookupFieldGroup({
		label: "With trailing icon",
		placeholder: "ID or code",
		fetchById: (id) =>
			new Promise((resolve) =>
				setTimeout(() => resolve({ id, label: `Fetched: ${id}` }), 300)
			),
		onResult: () => {},
		trailingIcon: ICON_CELL,
		trailingIconAriaLabel: "Use selected cell",
		onTrailingIconClick: (runLookup) => runLookup("DEMO-ID"),
	})
	lookupDemos.appendChild(lookupFieldWithIcon.root)
	lookupSection.appendChild(lookupDemos)
	root.appendChild(lookupSection)

	// Tabset — primary + secondary (flex row with wrap, stack when narrow)
	const tabsetSection = createCard({ title: "Tabset", padding: "md" })
	const tabsetDemos = document.createElement("div")
	tabsetDemos.className = "component-demos component-demos--tabsets"
	const primaryPanels = new Map<string, HTMLElement>([
		["one", document.createElement("div")],
		["two", document.createElement("div")],
	])
	primaryPanels.get("one")!.textContent = "Primary tab 1 content."
	primaryPanels.get("two")!.textContent = "Primary tab 2 content."
	const primaryTabset = createTabset({
		tabs: [{ id: "one", label: "Tab One" }, { id: "two", label: "Tab Two" }],
		activeTabId: "one",
		type: "primary",
		getPanel: (id) => primaryPanels.get(id),
		ariaLabel: "Primary tabs",
	})
	tabsetDemos.appendChild(primaryTabset.root)
	const secondaryPanels = new Map<string, HTMLElement>([
		["a", document.createElement("div")],
		["b", document.createElement("div")],
	])
	secondaryPanels.get("a")!.textContent = "Secondary tab A content."
	secondaryPanels.get("b")!.textContent = "Secondary tab B content."
	const secondaryTabset = createTabset({
		tabs: [{ id: "a", label: "A" }, { id: "b", label: "B" }],
		activeTabId: "a",
		type: "secondary",
		getPanel: (id) => secondaryPanels.get(id),
		ariaLabel: "Secondary tabs",
	})
	tabsetDemos.appendChild(secondaryTabset.root)
	tabsetSection.appendChild(tabsetDemos)
	root.appendChild(tabsetSection)

	// Badge — static and removable filter chips
	const badgeSection = createCard({ title: "Badge", padding: "md" })

	function demoRow(): HTMLElement {
		const el = document.createElement("div")
		el.style.cssText = "display:flex;flex-wrap:wrap;gap:0.5rem;align-items:center;"
		return el
	}
	function demoLabel(text: string): HTMLElement {
		const el = document.createElement("p")
		el.style.cssText =
			"margin:0 0 0.375rem;font-size:var(--ds-font-size-xs);color:var(--ds-color-text-subtle);font-weight:var(--ds-font-weight-medium);"
		el.textContent = text
		return el
	}
	function demoGroup(): HTMLElement {
		const el = document.createElement("div")
		el.style.marginBottom = "0.875rem"
		return el
	}

	// Static (no remove button)
	const staticGroup = demoGroup()
	staticGroup.appendChild(demoLabel("Static"))
	const staticRow = demoRow()
	for (const label of ["Active", "Positions", "Fund ID", "Market Value > 500k"]) {
		staticRow.appendChild(createBadge({ label }).root)
	}
	staticGroup.appendChild(staticRow)

	// Removable — clicking × removes the badge (demo: self-remove)
	const removableGroup = demoGroup()
	removableGroup.style.marginBottom = "0"
	removableGroup.appendChild(demoLabel("Removable (filter chips) — click × to remove"))
	const removableRow = demoRow()
	for (const label of ["Price = 1.05", "Fund ID starts with TCW", "Quantity ≥ 1 000 000", "As Of > 2024-01-01"]) {
		const b = createBadge({ label, onRemove: () => b.root.remove(), removeAriaLabel: `Remove filter: ${label}` })
		removableRow.appendChild(b.root)
	}
	removableGroup.appendChild(removableRow)

	badgeSection.appendChild(staticGroup)
	badgeSection.appendChild(removableGroup)
	root.appendChild(badgeSection)

	// Checkbox — interactive and read-only variants
	const checkboxSection = createCard({ title: "Checkbox", padding: "md" })
	const checkboxDemos = document.createElement("div")
	checkboxDemos.className = "component-demos"

	const interactiveGroup = demoGroup()
	interactiveGroup.appendChild(demoLabel("Interactive"))
	const interactiveRow = demoRow()
	interactiveRow.style.flexDirection = "column"
	interactiveRow.style.alignItems = "flex-start"
	interactiveRow.appendChild(createCheckbox({ label: "Unchecked", checked: false }).root)
	interactiveRow.appendChild(createCheckbox({ label: "Checked", checked: true }).root)
	interactiveGroup.appendChild(interactiveRow)

	const readonlyGroup = demoGroup()
	readonlyGroup.style.marginBottom = "0"
	readonlyGroup.appendChild(demoLabel("Read-only (non-interactive, full color)"))
	const readonlyRow = demoRow()
	readonlyRow.style.flexDirection = "column"
	readonlyRow.style.alignItems = "flex-start"
	readonlyRow.appendChild(createCheckbox({ label: "Read-only false", checked: false, readonly: true }).root)
	readonlyRow.appendChild(createCheckbox({ label: "Read-only true", checked: true, readonly: true }).root)
	readonlyGroup.appendChild(readonlyRow)

	checkboxDemos.appendChild(interactiveGroup)
	checkboxDemos.appendChild(readonlyGroup)
	checkboxSection.appendChild(checkboxDemos)
	root.appendChild(checkboxSection)

	return root
}
